import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import Cookies from "js-cookie";
import introJs from 'intro.js';

// Datatables imports
import DataTablesCore from 'datatables.net-bs5';
import DataTable from 'datatables.net-react';
import 'datatables.net-buttons-bs5';
import 'datatables.net-buttons/js/buttons.html5.mjs';
import 'datatables.net-buttons/js/buttons.print.mjs';
import 'datatables.net-responsive-bs5';
import FoodPantry from '../foodPantry/FP_FoodPantry';

function Profile({ checkedOutProfileClientIDs, userPermissions, setContentOpen, profileTourKey, url }) {

  // Card definitions for rendering
  const statCardFields = [
    { title: "Last Visit", key: "lastVisit", isLastVisit: true },
    { title: "Date of Birth", key: "DOB", isDate: true },
    { title: "Address", key: "addressLine1" }, // Only show addressLine1
    { title: "Email", key: "email" },
    { title: "Phone", key: "phone" },
    { title: "Member Since", key: "clientSince", isDate: true },
  ];

  // Helper to get last visit date from visitHistoryData
  const getLastVisitDate = () => {
    if (visitHistoryData.length === 0) return "N/A";
    const sorted = [...visitHistoryData].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted[0].date;
  };

  // Helper function to check validity
  const isValidClientID = id =>
    id && id !== "null" && id !== "undefined" && id !== "j:null";

  // Build array from checkedOutProfileClientIDs and cookie
  let cookieClientID = Cookies.get('clientID');
  const clientIDsArr = [
    ...checkedOutProfileClientIDs.filter(isValidClientID),
    ...(isValidClientID(cookieClientID) ? [cookieClientID] : [])
  ];

  if (clientIDsArr.length === 0) clientIDsArr.push("NO CLIENT ID");

  const [clientIDs, setClientIDs] = useState(clientIDsArr);
  const [clientIDsIndex, setClientIDsIndex] = useState(0);

  useEffect(() => {
    setClientIDs(clientIDsArr);
    setClientIDsIndex(0); // Reset index when IDs change
  }, [checkedOutProfileClientIDs]);

  const [showModal, setShowModal] = useState(false);

  const [QRCodeLink, setQRCodeLink] = useState(clientIDsArr[0] || "ERROR");


  const [userProfile, setUserProfile] = useState({ "nothing": "Provided" });


  // Phone validation regex
  const phoneReg = /^\(\d{3}\) \d{3}-\d{4}$/;

  // Helper to format phone input
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Form state for phone and preferred contact
  const [formPhone, setFormPhone] = useState(userProfile.phone ? formatPhone(userProfile.phone) : "");
  const [formPreferredContact, setFormPreferredContact] = useState(userProfile.preferredContact || "Email");
  const [formDOB, setFormDOB] = useState(userProfile.DOB || "");
  const [formFirstName, setFormFirstName] = useState(userProfile.firstName || "");
  const [formLastName, setFormLastName] = useState(userProfile.lastName || "");
  const [formError, setFormError] = useState("");
  // Add form states for addressLine2, city, state, zip
  const [formAddressLine2, setFormAddressLine2] = useState(userProfile.addressLine2 || "");
  const [formCity, setFormCity] = useState(userProfile.city || "");
  const [formState, setFormState] = useState(userProfile.state || "");
  const [formZip, setFormZip] = useState(userProfile.zip || "");

  const [visitHistoryData, setVisitHistoryData] = useState([
    { id: 1, date: "October 10, 2025", serviceTypeName: "Food Pantry" },
    { id: 2, date: "September 25, 2025", serviceTypeName: "Medical" },
    { id: 3, date: "September 10, 2025", serviceTypeName: "Shelter" },
    { id: 4, date: "August 28, 2025", serviceTypeName: "Food" },
    { id: 5, date: "August 14, 2025", serviceTypeName: "Shelter" },
    { id: 6, date: "July 30, 2025", serviceTypeName: "Food Pantry" },
    { id: 7, date: "July 15, 2025", serviceTypeName: "Medical" },
    { id: 8, date: "June 29, 2025", serviceTypeName: "Medical" },
  ]);

  const visitHistoryColumns = [
    { title: 'Date', data: 'date' },
    { title: 'Visit Type', data: 'serviceTypeName' },
  ];

  const cardShadowStyle = {
    boxShadow: "0 6px 15px rgba(0, 0, 0, 0.1)",
  };

  useEffect(() => {
    // 1/3 chance for joke, otherwise QR code is client ID
    const randomNumber = Math.floor(Math.random() * 3) + 1;
    if (randomNumber === 1 && localStorage.getItem('BurchCoin')) {
      setQRCodeLink(clientIDs[clientIDsIndex]);
    } else {
      setQRCodeLink(clientIDs[clientIDsIndex]);
    }
  }, [showModal, clientIDs, clientIDsIndex]);

  useEffect(() => {
    // The hardcoded ID is used directly in the fetch call URL
    fetch(url + `/api/foodPantry/client/profile?clientID=${clientIDs[clientIDsIndex]}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        //console.log('Fetched client profile:', data);
        const safeProfile = {
          name: "None Provided",
          addressLine1: "None Provided", // default for addressLine1
          addressLine2: "", // default for addressLine2
          city: "NA",
          state: "NA",
          zip: "NA",
          clientSince: "Unknown",
          lastVisit: "Unknown",
          familySize: "None Provided",
          email: "None Provided",
          phone: "None Provided",
          memberId: "None Provided",
          preferredContact: "None Provided",
          emergencyContact: "None Provided",
          allergies: "None Provided",
          notes: "None Provided",
          checkinCode: QRCodeLink,
          ...data.profile
        };
        setUserProfile(safeProfile);
      })
      .catch(error => console.error('Error fetching client profile:', error));

    // Fetch visit history data from new endpoint
    fetch(url + `/api/foodPantry/visit/${clientIDs[clientIDsIndex]}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        const formattedVisits = (data || [])
          .map((visit, idx) => ({
            ...visit,
            // format date
            date: visit.date
              ? new Date(visit.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              })
              : '',
            rawDate: visit.date ? new Date(visit.date) : null, // keep original for sorting
            id: idx + 1,
          }))
          // sort newest first
          .sort((a, b) => (b.rawDate || 0) - (a.rawDate || 0));

        setVisitHistoryData(formattedVisits);
      })
      .catch(error => console.error('Error fetching visit history:', error));
  }, [clientIDs, clientIDsIndex]);

  const handleCloseModal = () => setShowModal(false);

  const handleShowModal = () => {
    if (userProfile && userProfile.lastName) {

      // 1. Controlled Fields Synchronization (First Name, Last Name, Phone, Contact)
      setFormFirstName(userProfile.name || userProfile.firstName || ""); // Use 'name' for first name
      setFormLastName(userProfile.lastName || "");
      setFormPreferredContact(userProfile.preferredContact || "Email");
      setFormPhone(userProfile.phone ? formatPhone(userProfile.phone) : "");

      // 2. DOB Date Formatting: Convert ISO string to YYYY-MM-DD for <input type="date">
      if (userProfile.DOB) {
        const isoDate = new Date(userProfile.DOB);
        const formattedDate = isoDate.toISOString().split('T')[0];
        setFormDOB(formattedDate);
      } else {
        setFormDOB("");
      }
      // Fill address fields
      setFormAddressLine2(userProfile.addressLine2 || "");
      setFormCity(userProfile.city || "");
      setFormState(userProfile.state || "");
      setFormZip(userProfile.zip || "");

      // 3. Address Parsing (Fixes City, State, ZIP)
      const fullAddress = userProfile.addressLine1 || "";
      let addressLine1 = fullAddress;
      let city = "";
      let state = "";
      let zip = "";

      const commaIndex = fullAddress.lastIndexOf(',');

      if (commaIndex !== -1) {
        const cityAndStreet = fullAddress.substring(0, commaIndex).trim();
        const stateAndZip = fullAddress.substring(commaIndex + 1).trim();

        const stateZipMatch = stateAndZip.match(/^([A-Z]{2})\s*(\d{5,10})$/);

        if (stateZipMatch) {
          state = stateZipMatch[1];
          zip = stateZipMatch[2];

          const cityMatch = cityAndStreet.match(/(.*)\s+([A-Za-z\s]+)$/);

          if (cityMatch) {
            addressLine1 = cityMatch[1].trim();
            city = cityMatch[2].trim();
          } else {
            addressLine1 = cityAndStreet;
          }
        } else {
          addressLine1 = fullAddress;
        }
      }

      // 4. Update the main userProfile state with the parsed components.
      // This ensures the UNCONTROLLED fields (defaultValue={userProfile.city}) have data.
      // setUserProfile(prev => ({
      //   ...prev,
      //   addressLine1: addressLine1,
      //   city: city,
      //   state: state,
      //   zip: zip,
      // }));
    }

    // Finally, show the modal after data is prepared
    setShowModal(true);
  };

  const handleSaveChanges = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const firstName = formData.get('firstName');
    const lastName = formData.get('lastName');
    const addressLine1 = formData.get('addressLine1');
    const addressLine2 = formData.get('addressLine2');
    const city = formData.get('city');
    const state = formData.get('state');
    const zip = formData.get('zip');
    const emailVal = formData.get('email');
    const phoneVal = formPhone;
    const preferredContactVal = formPreferredContact;
    const notesVal = formData.get('notes');
    const familySizeVal = formData.get('familySize');
    const DOBVal = formData.get('DOB');

    // Validate phone only
    if (!phoneReg.test(phoneVal)) {
      setFormError("Phone number must be in format (555) 555-5555");
      return;
    }
    setFormError("");
    // Validate preferredContact (limit to allowed values)
    const allowedContacts = ["Email", "Phone"];
    const safePreferredContact = allowedContacts.includes(preferredContactVal) ? preferredContactVal : "Email";

    // Parse phone
    const digits = phoneVal.replace(/\D/g, "");
    const phone = {
      areaCode: digits.slice(0, 3),
      telPrefix: digits.slice(3, 6),
      lineNum: digits.slice(6, 10),
    };
    // Build client object for API
    const client = {
      clientID: clientIDs[clientIDsIndex],
      firstName: firstName || "",
      lastName: lastName || "",
      preferredContact: safePreferredContact,
      note: notesVal,
      familySize: familySizeVal,
      DOB: DOBVal,
    };
    const address = {
      addressLine1,
      addressLine2,
      city,
      state,
      zip,
    };
    const user = {
      email: emailVal,
    };
    try {
      const response = await fetch(url + "/api/foodPantry/client", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client, address, phone, user })
      });
      if (!response.ok) {
        const data = await response.json();
        alert(data.message || "Failed to update client");
        return;
      }
      // Update local state with only supported fields
      setUserProfile(prev => ({
        ...prev,
        firstName,
        lastName,
        addressLine1,
        addressLine2,
        city,
        state,
        zip,
        email: emailVal,
        phone: phoneVal,
        preferredContact: safePreferredContact,
        notes: notesVal,
        familySize: familySizeVal,
        DOB: DOBVal,
      }));
      handleCloseModal();
      window.location.reload();

    } catch (err) {
      alert("Failed to update client: " + err.message);
    }
  };

  const StatCard = ({ title, value }) => {
    const cardStyle = {
      width: "100%",
      height: "50px",
      backgroundColor: "white",
      borderRadius: "10px",
      padding: "10px",
      color: "black",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      border: "1px solid #ced4da",
      flexShrink: 0,
    };
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: "14px", paddingRight: "10px", paddingLeft: "10px" }}><span style={{ fontWeight: "bold" }}>{title}</span>: {value}</div>
      </div>
    );
  };


  useEffect(() => {
    if (!userPermissions) return;

    const fullTourNeeded = JSON.parse(localStorage.getItem("FullTourNeeded"));
    if (!fullTourNeeded) return;

    // Helper to check if all tour elements are present
    function areTourElementsReady() {
      // List all selectors for your tour steps (adjust as needed)
      const selectors = [
        '[data-step="1"]',
        '[data-step="2"]',
        '[data-step="3"]',
        '[data-step="4"]'
      ];
      return selectors.every(sel => document.querySelector(sel));
    }

    // Poll until all tour elements are present, then start the tour
    let pollInterval = null;
    function tryStartTour() {
      // Check if an Intro.js tour is already started
      const introInstance = window.introJs && window.introJs();
      const isTourActive = introInstance && introInstance._currentStep !== undefined;
      if (areTourElementsReady() && !isTourActive) {
        clearInterval(pollInterval);
        const intro = introJs();
        // wait a second before starting
        setTimeout(() => {
          intro.setOptions({
            showBullets: false // <-- Add this line to hide bullets
          });
          intro.start();
          intro.onComplete(() => {
            if (userPermissions.includes("clientSearch")) {
              setContentOpen("searchClient");
            } else if (userPermissions.includes("registerClient")) {
              setContentOpen("registerClient");
            } else if (userPermissions.includes("foodPantry")) {
              setContentOpen("foodPantry");
            } else if (userPermissions.includes("reports")) {
              setContentOpen("reports");
            } else {
              setContentOpen("searchOrganizations");
            }
          });
        }, 1000);
      }
    }

    pollInterval = setInterval(tryStartTour, 200);

    // Cleanup interval on unmount
    return () => clearInterval(pollInterval);
  }, [userPermissions, setContentOpen, profileTourKey]);



  return (
    <>
      {/* {clientIDs[0] === "NO CLIENT ID" && (
        <div className="alert alert-warning m-3" role="alert">
          No valid client ID found. Please select a client from the search page.
        </div>
      )} */}

      {/* {clientIDs[0] !== "NO CLIENT ID" && clientIDs.length >= 1 && ( */}
      <div className="card col-12 col-md-10 offset-0 offset-md-1 mt-5">
        <div className="container my-3">
          <div className="card-body">
            {clientIDs.length > 1 && (
              <div className="row mb-2">
                <div
                  className="col d-flex"
                  style={{
                    justifyContent: window.innerWidth < 1200 ? "space-between" : "center",
                    alignItems: "center"
                  }}
                >
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (clientIDsIndex > 0) {
                        setClientIDsIndex(clientIDsIndex - 1);
                        setQRCodeLink(clientIDs[clientIDsIndex - 1]);
                      }
                    }}
                  >
                    <span>&lt;</span>
                  </button>
                  <span className="ms-3 me-3" style={{ minWidth: "120px", display: "inline-block", textAlign: "center" }}>
                    {clientIDs.length > 1 && (
                      <span>
                        Viewing {clientIDsIndex + 1} of {clientIDs.length}
                      </span>
                    )}
                  </span>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (clientIDsIndex < clientIDs.length - 1) {
                        setClientIDsIndex(clientIDsIndex + 1);
                        setQRCodeLink(clientIDs[clientIDsIndex + 1]);
                      }
                    }}
                  >
                    <span>&gt;</span>
                  </button>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-5">
              <h2 className="text-info">
                <span style={{
                  backgroundImage: "linear-gradient(90deg, #3d58e7 0%, #2575fc 60%, #5595fc  100%)", /* Deep Blue -> Orange -> Sky Blue */
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent", // Fallback color
                  fontWeight: "700",
                }}
                  data-intro="This is your profile page where you can view and edit your personal information, see your check-in QR code, and review your visit history."
                  data-step="1">
                  <i className="bi bi-stars"></i> Hello, {userProfile.name}
                  <i
                    className="bi bi-question-circle d-none d-sm-inline"
                    style={{
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: '#4d59df',
                      marginRight: '0.75rem',
                      marginLeft: '0.25rem'
                    }}
                    onClick={() => {
                      const ManualIntro = introJs();

                      // ManualIntro.onComplete(() => {
                      //   console.log('Tour completed!');
                      // });
                      // ManualIntro.onExit(() => {
                      //   console.log('Tour exited!');
                      // });

                      ManualIntro.start();
                    }}
                  />
                </span>
              </h2>
              <Button
                variant="primary"
                onClick={handleShowModal}
                className="d-flex align-items-center justify-content-center"
                style={{
                  minWidth: "67px",
                  minHeight: "44px",
                  fontWeight: 500,
                  fontSize: "1rem",
                  gap: "8px"
                }}
                data-intro="Click here to edit your profile information."
                data-step="3"
              >
                <FontAwesomeIcon icon={faEdit} />
                <span className="d-none d-md-inline ms-2">Edit Profile</span>
              </Button>
            </div>
            <div
              className="profile-flex-container d-flex flex-column flex-md-row align-items-center pt-2"
              style={{ gap: "15px" }}
              data-intro="Here is your check-in QR code along with your profile information."
              data-step="2"
            >
              {/* QR Code Section */}
              <div
                style={{
                  backgroundColor: "white",
                  padding: "15px",
                  borderRadius: "10px",
                  border: "1px solid #ced4da",
                  ...cardShadowStyle,
                  width: "100%",
                  maxHeight: "350px",
                  maxWidth: "350px",
                }}
                className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
              >
                <div className='p-4'>
                  <h6 className="text-center mb-3">Check-In QR Code</h6>
                  <QRCode
                    value={Cookies.get("userID") || QRCodeLink}
                    viewBox={`0 0 256 256`}
                  />
                </div>
              </div>
              {/* Profile Information Section */}
              <div
                className="d-flex flex-wrap align-items-start justify-content-start"
                style={{
                  gap: "10px", flexDirection: 'column',
                  width: 'max-content'
                }}
              >
                {statCardFields.map((field, index) => {
                  let value;
                  if (field.isLastVisit) {
                    value = getLastVisitDate();
                  } else if (field.key === "familySize") {
                    value = `${userProfile.familySize} people`;
                  } else if (field.isDate && userProfile[field.key]) {
                    // Format ISO date string to readable format
                    const dateObj = new Date(userProfile[field.key]);
                    value = dateObj.toLocaleDateString(undefined, {
                      year: 'numeric', month: 'long', day: 'numeric'
                    });
                  } else {
                    value = userProfile[field.key];
                  }
                  return <StatCard key={index} title={field.title} value={value} />;
                })}
              </div>
            </div>
            {/* Visit History DataTable */}
            <div className='text-dark mt-5'
              data-intro="This table shows your check-in history."
              data-step="4">
              <h5 className="mb-2 text-dark">Check-In History</h5>
              <div

              >
                <DataTable
                  data={visitHistoryData}
                  columns={visitHistoryColumns}
                  options={{
                    paging: true,
                    pageLength: 4,
                    searching: true,
                    ordering: true,
                    responsive: true,
                    dom: 'Bfrtip',
                    order: [[0, 'desc']],
                    buttons: [
                      {
                        extend: 'collection',
                        text: 'Export',
                        buttons: [
                          { extend: 'excel', text: 'Export to Excel' },
                          { extend: 'pdf', text: 'Export to PDF' },
                          { extend: 'print', text: 'Print' }
                        ]
                      }
                    ],
                    language: {
                      info: "_START_-_END_ of _TOTAL_ items",
                      infoEmpty: "No data available",
                      infoFiltered: "(filtered from _MAX_ total items)",
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* )} */}

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header>
          <div className="d-flex align-items-center justify-content-between w-100">
            <Modal.Title id="create-item-variation-modal">
              Edit Profile
            </Modal.Title>
            {/* Close button for modal */}
            <Button
              variant="light"
              onClick={handleCloseModal}
            >
              <i className="fa fa-times"></i>
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleSaveChanges}>
            <h5 className="mb-3 mt-1">Personal Info</h5>
            <div className="row g-2">
              {/* FIRST NAME */}
              <div className="col-md-6">
                <div className="mb-3 form-floating">
                  <input
                    name="firstName"
                    type="text"
                    placeholder="First Name" // Placeholder required for form-floating
                    className="form-control"
                    value={formFirstName}
                    onChange={e => setFormFirstName(e.target.value)}
                    required
                  />
                  <label htmlFor="formFirstName">First Name</label>
                </div>
              </div>

              {/* LAST NAME */}
              <div className="col-md-6">
                <div className="mb-3 form-floating">
                  <input
                    name="lastName"
                    type="text"
                    placeholder="Last Name"
                    className="form-control"
                    value={formLastName}
                    onChange={e => setFormLastName(e.target.value)}
                    required
                  />
                  <label htmlFor="formLastName">Last Name</label>
                </div>
              </div>
            </div>

            {/* DOB and Family Size */}
            <div className="row g-2">
              {/* DATE OF BIRTH */}
              {/* <div className="col-md-6"> */}
              <div className="mb-3 form-floating">
                <input
                  name="DOB"
                  type="date"
                  placeholder="Date of Birth"
                  className="form-control"
                  value={formDOB}
                  onChange={e => setFormDOB(e.target.value)}
                  required
                />
                <label htmlFor="formDOB">Date of Birth</label>
              </div>
              {/* </div> */}

              {/* {/* FAMILY SIZE 
              <div className="col-md-6">
                <div className="mb-3 form-floating">
                  <input
                    name="familySize"
                    type="number"
                    placeholder="Family Size"
                    className="form-control"
                    min={1}
                    max={20}
                    defaultValue={userProfile.familySize || 1}
                    required
                  />
                  <label htmlFor="formFamilySize">Family Size</label>
                </div>
              </div> */}
            </div>

            <h5 className="mb-3 mt-1">Contact Info</h5>

            {/* ADDRESS LINE 1 */}
            <div className="mb-3 form-floating">
              <input
                name="addressLine1"
                type="text"
                placeholder="Address Line"
                className="form-control"
                defaultValue={userProfile.addressLine1 || ""}
                maxLength={100}
                onFocus={(e) => e.target.select()}
                required
              />
              <label htmlFor="formAddressLine1">Address</label>
            </div>

            {/* ADDRESS LINE 2 - Kept in standard non-floating style as it was before, but added mb-3 */}
            <div className="mb-3 form-floating">
              <input
                name="addressLine2"
                type="text"
                placeholder="Apt, Suite, etc. (optional)"
                className="form-control"
                value={formAddressLine2}
                onChange={e => setFormAddressLine2(e.target.value)}
                maxLength={100}
                onFocus={(e) => e.target.select()}
              />
              <label htmlFor="formAddressLine2">Apt, Suite, etc. (optional)</label>
            </div>

            {/* CITY, STATE, ZIP */}
            <div className="row g-2">
              {/* City */}
              <div className="col-md-6">
                <div className="mb-3 form-floating">
                  <input
                    name="city"
                    type="text"
                    placeholder="City"
                    className="form-control"
                    value={formCity}
                    onChange={e => setFormCity(e.target.value)}
                    maxLength={50}
                    required
                  />
                  <label htmlFor="formCity">City</label>
                </div>
              </div>

              {/* State */}
              <div className="col-md-3">
                <div className="mb-3 form-floating">
                  <input
                    name="state"
                    type="text"
                    placeholder="State"
                    className="form-control"
                    value={formState}
                    onChange={e => setFormState(e.target.value)}
                    maxLength={2}
                    required
                  />
                  <label htmlFor="formState">State</label>
                </div>
              </div>

              {/* Zip */}
              <div className="col-md-3">
                <div className="mb-3 form-floating">
                  <input
                    name="zip"
                    type="text"
                    placeholder="Zip Code"
                    className="form-control"
                    value={formZip}
                    onChange={e => setFormZip(e.target.value)}
                    maxLength={10}
                    required
                  />
                  <label htmlFor="formZip">Zip</label>
                </div>
              </div>
            </div>

            {/* EMAIL */}
            <div className="mb-3 form-floating">
              <input
                name="email"
                type="email"
                placeholder="Email"
                className="form-control"
                defaultValue={userProfile.email}
              />
              <label htmlFor="formEmail">Email</label>
            </div>

            {/* PHONE */}
            <div className="mb-3 form-floating">
              <input
                name="phone"
                type="tel"
                placeholder="(555) 555-5555"
                className="form-control"
                value={formPhone}
                onChange={e => setFormPhone(formatPhone(e.target.value))}
                onFocus={(e) => e.target.select()}
                pattern="\(\d{3}\) \d{3}-\d{4}"
                required
                aria-label="Enter your phone number in format (555) 555-5555"
              />
              <label htmlFor="formPhone">Phone</label>
            </div>

            {/* {/* PREFERRED CONTACT (Select) 
            <div className="mt-4">
              <label htmlFor="formPreferredContact">Preferred Contact</label>
              <select
                name="preferredContact"
                className="form-select"
                value={formPreferredContact}
                onChange={e => setFormPreferredContact(e.target.value)}
                required
              >
                <option value="Email">Email</option>
                <option value="Phone">Phone</option>
              </select>
              {formError && <div className="alert alert-danger">{formError}</div>}
            </div> */}



            <div className="d-flex gap-2 justify-content-end mt-4">
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

    </>
  );
}

export default Profile;
